import { useRef, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsFocused } from '@react-navigation/native';
import { EventsPerspective } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { TimeTilesProps } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles';
import { REMEASURING_AFTER_ANIMATION_TIMING } from '@v2/pages/common/components/NCFileBatchEvents/components/TimeTiles/constants';
import { selectSingleBatch } from '@reducers/batch';
import { selectSelectedItem } from '@reducers/machines';
import { ProductionBatchStatistics } from '@interfaces/search';
import { Machine } from '@interfaces/machines';
import { useCurveLinesContextOptional } from '@v2/pages/common/components/NCFileBatchEvents/context/CurveLinesContext';
import { useManageTimeTilesConfig } from './useManageTimeTilesConfig';

interface UseManageTimeTilesParams extends Pick<TimeTilesProps, 'eventTimelineData' | 'eventsPerspective' | 'ncFileId' | 'isDataLoaded'> {}

const useManageTimeTiles = ({ eventTimelineData, eventsPerspective, ncFileId, isDataLoaded }: UseManageTimeTilesParams) => {
  const batchData: ProductionBatchStatistics = useSelector(selectSingleBatch);
  const selectedMachine: Machine = useSelector(selectSelectedItem);

  const curveLinesContext = useCurveLinesContextOptional();
  const bumpMeasurementVersion = curveLinesContext?.bumpMeasurementVersion;
  const isResizing = curveLinesContext?.isResizing ?? false;

  const updatedNcFileIdConfig: Partial<Record<EventsPerspective, number | null | undefined>> = {
    [EventsPerspective.BATCH]: batchData?.ncFileId,
    [EventsPerspective.NC_FILE]: ncFileId,
    [EventsPerspective.SINGLE_MACHINE]: selectedMachine?.machineStatus?.ncFileId,
  };

  const ncFileIdForUpdate = eventsPerspective ? updatedNcFileIdConfig[eventsPerspective] : null;

  const { timeTilesConfig } = useManageTimeTilesConfig({ eventTimelineData });

  // isReadyToShow should be true when data has loaded (even if empty)
  // If isDataLoaded is provided, use it; otherwise fall back to checking eventTimelineData existence
  const isReadyToShow = isDataLoaded ?? eventTimelineData != null;

  // Use focus state to detect when tab becomes active
  const isFocused = useIsFocused();
  const wasFocusedAndReady = useRef(false);
  const [animationEnabled, setAnimationEnabled] = useState(false);

  // Track sort order for remeasure trigger
  const currentOrder = useMemo(() => timeTilesConfig.map((c) => c.timeCategory).join(','), [timeTilesConfig]);
  const previousOrder = useRef(currentOrder);
  const [remeasureTrigger, setRemeasureTrigger] = useState(0);
  const wasReadyToShow = useRef(isReadyToShow);
  const wasFocused = useRef(isFocused);

  // When tab becomes focused AND has data, disable animation temporarily to prevent "flying in"
  useEffect(() => {
    if (isFocused && isReadyToShow) {
      if (!wasFocusedAndReady.current) {
        // Just became focused with data - disable animation temporarily
        setAnimationEnabled(false);
        const timer = setTimeout(() => {
          setAnimationEnabled(true);
          wasFocusedAndReady.current = true;
        }, 100);
        return () => clearTimeout(timer);
      }
    }

    if (!isFocused) {
      // Lost focus - reset state so animation is disabled when we regain focus
      wasFocusedAndReady.current = false;
      setAnimationEnabled(false);
    }
  }, [isFocused, isReadyToShow]);

  const shouldAnimate = animationEnabled && isFocused && isReadyToShow && !isResizing;

  // Reanimated's SequencedTransition has no "done" callback, so we still time the swap-animation duration.
  useEffect(() => {
    if (previousOrder.current !== currentOrder && shouldAnimate) {
      const timer = setTimeout(() => {
        setRemeasureTrigger((prev) => prev + 1);
        bumpMeasurementVersion?.();
      }, REMEASURING_AFTER_ANIMATION_TIMING);
      previousOrder.current = currentOrder;
      return () => clearTimeout(timer);
    }
    previousOrder.current = currentOrder;
  }, [currentOrder, shouldAnimate, bumpMeasurementVersion]);

  useEffect(() => {
    if (!wasReadyToShow.current && isReadyToShow && isFocused) {
      setRemeasureTrigger((prev) => prev + 1);
      bumpMeasurementVersion?.();
    }
    wasReadyToShow.current = isReadyToShow;
  }, [isReadyToShow, isFocused, bumpMeasurementVersion]);

  useEffect(() => {
    if (!wasFocused.current && isFocused && isReadyToShow) {
      setRemeasureTrigger((prev) => prev + 1);
      bumpMeasurementVersion?.();
    }
    wasFocused.current = isFocused;
  }, [isFocused, isReadyToShow, bumpMeasurementVersion]);

  return {
    timeTilesConfig,
    isReadyToShow,
    ncFileIdForUpdate,
    shouldAnimate,
    remeasureTrigger,
  };
};

export { useManageTimeTiles };
